/**
 * 组织下拉按「主体（厂）」分组、按层级缩进（0923 Tony:「组织下拉里面很乱」）。
 *
 * 53 个真部门原来平铺成一长串：三个厂各有自己的制造部、质量部、计划物流部，
 * 挨着出现、只靠括号里的上一级区分；「注塑车间（制造部）」JQAT 和 JQHX 各一个，
 * 连括号都一样。现在先按主体分组，组内按路径排（父在子前）、按层级缩进。
 *
 * 主体是后端按组织路径第一段算好的（org-judgement.ts 的 orgEntityOf）——
 * 前端不另判一套，只负责摆。
 */

export interface OrgUnitOption {
  id: number;
  name: string;
  label?: string;
  memberCount?: number;
  entity?: string;
  depth?: number;
}

export interface OrgUnitGroup<T extends OrgUnitOption = OrgUnitOption> {
  /** 分组标题（主体名，带上所在地） */
  title: string;
  entity: string;
  items: Array<T & { text: string }>;
}

/** 三家子公司在哪儿（CLAUDE.md：JQAT 奉化、JQHX 横溪、JQSN 首南） */
const ENTITY_PLACE: Readonly<Record<string, string>> = {
  JQAT饰件科技: '奉化',
  JQHX东元塑胶: '横溪',
  JQSN均源科技: '首南',
};

const HEADQUARTERS = '东禾本部';
const UNKNOWN = '未归属';

function entityRank(entity: string): number {
  if (entity === HEADQUARTERS) return 0;
  if (entity === UNKNOWN) return 2;
  return 1;
}

/** 选项上显示的字：缩进 + 名字（上一级）+ 人数 */
export function orgOptionText(unit: OrgUnitOption, withCount = true): string {
  const depth = Math.max(unit.depth ?? 0, 0);
  const indent = '　'.repeat(depth);
  const name = unit.label || unit.name;
  const count = withCount && unit.memberCount !== undefined ? `（${unit.memberCount} 人）` : '';
  return `${indent}${name}${count}`;
}

/**
 * 按主体分组。本部在最前、三家子公司按名字、归不到主体的在最后。
 * 组内保持后端给的顺序（后端按 path 排，父一定在子前面）。
 * 老后端不带 entity 时整份归到一组，行为和原来一样。
 */
export function groupOrgUnits<T extends OrgUnitOption>(
  units: readonly T[],
  withCount = true,
): Array<OrgUnitGroup<T>> {
  const groups = new Map<string, OrgUnitGroup<T>>();
  for (const unit of units) {
    const entity = unit.entity || UNKNOWN;
    let group = groups.get(entity);
    if (!group) {
      const place = ENTITY_PLACE[entity];
      group = { entity, title: place ? `${entity}（${place}）` : entity, items: [] };
      groups.set(entity, group);
    }
    group.items.push({ ...unit, text: orgOptionText(unit, withCount) });
  }
  return [...groups.values()].sort((left, right) => {
    const rank = entityRank(left.entity) - entityRank(right.entity);
    return rank || left.entity.localeCompare(right.entity, 'zh-Hans-CN');
  });
}
